import * as Sentry from '@sentry/react'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import BodyClassName from 'react-body-classname'
import 'react-reflex/styles.css'
import LoadingSpinner from '../../../../../components/common/LoadingSpinner'
import { TAB } from '../../../../../components/materials/const'
import GoogleTranslate from '../../../../../components/new-portal/GoogleTranslate'
import PreparationTab from '../../../../../components/new-portal/lessons/preparation/PreparationTab'
import Textbody from '../../../../../components/new-portal/lessons/preparation/TextBody'
import UnifiedTextbody from '../../../../../components/new-portal/lessons/preparation/UnifiedTextBody'
import { handleSubmitAnswer } from '../../../../../components/new-portal/materials/QuestionSelect/QuestionSelectPanel'
import { useEventTracking } from '../../../../../helpers/gtmHelper'
import { useLessonPreparationV5 } from '../../../../../modules/apis/student/lesson'
import { useCurrentUser } from '../../../../../modules/context/UserContext'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import { LOCALE_EN, LOCALE_KO } from '../../../../../modules/i18n/config'
import { n_lesson_prep } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { PRE_STUDY } from '../../../../../modules/vars'

export const paneMaxHeight = 'calc(100vh - 120px)'

// subtab 이동 시에도 답변 저장
interface ISaveSubMove {
  isEditable: any
  setIsEditable: any
  activeQuestionId: any
  answer: any
  lesson: any
}

export const saveMoveSubTab = async ({ isEditable, setIsEditable, activeQuestionId, answer, lesson }: ISaveSubMove) => {
  const updateIsEditable = [...isEditable]
  updateIsEditable.map((item, idx) => {
    if (item.question_number == activeQuestionId) {
      const currentAnswer = isEditable?.find((item, idx) => Number(item.question_number) == activeQuestionId)
      if (currentAnswer) {
        handleSubmitAnswer({
          id: currentAnswer.id,
          content: answer,
          shareStatus: currentAnswer.student_answer?.share_status,
          isEditable,
          setIsEditable,
          activeQuestionId,
          lessonId: lesson.id,
        })
      } else {
        console.log('error')
      }
    }
  })
}

export const preventClose = (e, message) => {
  // const message = t(n_lesson_prep.popup_leave_default)
  e.returnValue = message
  return message
}

const Preparation = () => {
  const router = useRouter()
  const { sendPageViewEvent } = useEventTracking()

  const isReview = router.query?.isReview ? router.query?.isReview : false

  const { t, locale } = useTranslation()
  const { currentUser } = useCurrentUser()
  const { data, error, isFetching, isLoading } = useLessonPreparationV5()

  // isEditable 정의: 예정된 수업에서 나갈 때 이 정보가 필요함
  // answer: 각각의 textarea 에 있는 값
  const [isEditable, setIsEditable] = useState([])
  const [answer, setAnswer] = useState('')
  const [selectedShareState, setSelectedShareState] = useState<number>(0)

  const getDefaultTab = () => {
    const typeQuery = router?.query?.type ? router?.query?.type : PRE_STUDY.INSIGHT
    switch (typeQuery) {
      case PRE_STUDY.EXPRESSION:
        return TAB.EXPRESSIONS
      case PRE_STUDY.QUESTION:
        return TAB.QUESTIONS
      case PRE_STUDY.ARTICLE:
        return TAB.MEDIA
      case PRE_STUDY.YOUTUBE:
        return TAB.MEDIA
      case PRE_STUDY.INSIGHT:
        return TAB.INSIGHT
      case PRE_STUDY.UPLOAD:
        return TAB.UPLOAD_FILE
      case PRE_STUDY.UPLOAD_FILE:
        return TAB.INSIGHT
      default:
        return TAB.INSIGHT
    }
  }

  const [currentTab, setCurrentTab] = useState(TAB.INSIGHT)
  const [currentTabSub, setCurrentTabSub] = useState(TAB.QUESTION_SELECT)
  const [unifiendCurrentTab, setUnifiedCurrentTab] = useState(TAB.INSIGHT)
  const [tabData, setTabData] = useState([])
  const [showGoogleTranslator, setShowGoogleTranslator] = useState(false)
  const [showTooltip, setShowTooltip] = useState(true)
  const [activeQuestionId, setActiveQuestionId] = useState(null)
  const isMobile = useIsMobile(undefined, 1040)

  // 답변이 저장되지 않은 것이 있을 때만 reload, 및 뒤로 가기 경고창 뜰 수 있도록
  let count = 0
  for (const item of isEditable) {
    if (item.editable && item.student_answer?.content) {
      count++
    }
  }
  useEffect(() => {
    if (!router) return
    sendPageViewEvent({ page_name: 'lesson_prep' })
  }, [router.asPath])

  useEffect(() => {
    window.addEventListener('beforeunload', (e) => preventClose(e, t(n_lesson_prep.popup_leave_default)))

    return () => {
      window.removeEventListener('beforeunload', (e) => preventClose(e, t(n_lesson_prep.popup_leave_default)))
    }
  }, [isEditable]) // isEditable 상태가 변경될 때만 이벤트 리스너 재설정

  useEffect(() => {
    if (data && data.lesson) {
      const course = data.lesson.course

      setCurrentTab(
        getDefaultTab() == TAB.MEDIA
          ? TAB.MEDIA
          : getDefaultTab() == TAB.EXPRESSIONS
          ? TAB.EXPRESSIONS
          : getDefaultTab() == TAB.UPLOAD_FILE || course.free_talking
          ? TAB.UPLOAD_FILE
          : TAB.INSIGHT
      )

      setCurrentTabSub(course.free_talking ? TAB.NOTE : TAB.QUESTION_SELECT)
    }
  }, [data])

  useEffect(() => {
    if (error) {
      if (error.response) {
        Sentry.captureException('수업 예습 불러오기 실패', { extra: { error } })
        switch (error.response.status) {
          case 404: {
            alert(
              'The lesson does not exist. If you find this to be an error, please let Ringle Team know to further assist you with the issue.'
            )
            break
          }
          case 403: {
            alert(
              'The lesson has been either cancelled or booked by someone else. If you find this to be an error, please let Ringle Team know to further assist you with the issue.'
            )
            break
          }
          default: {
            alert('There is an error with your request. Please let Ringle Team know to further assist with this issue.')
          }
        }
      } else {
        alert('There is an error with your request. Please let Ringle Team know to further assist with this issue.')
      }

      router.back()
    }
  }, [error])

  if (!currentUser) {
    return <></>
  }

  const lesson = data?.lesson
  const isFreeTalkingOrWriting = data?.lesson?.course?.free_talking || data?.lesson?.course?.is_writing

  return (
    <div className="mx-auto w-full">
      <PreparationTab
        lesson={isFreeTalkingOrWriting ? lesson : tabData}
        isEditable={isEditable}
        setIsEditable={setIsEditable}
        originalLesson={lesson}
        showGoogleTranslator={showGoogleTranslator}
        setShowGoogleTranslator={setShowGoogleTranslator}
        showTooltip={showTooltip}
        setShowTooltip={setShowTooltip}
        isReview={isReview}
      />
      <BodyClassName className="body" />
      {isFetching && (
        <div className="flex h-screen w-screen items-center justify-center">
          <LoadingSpinner lg show primary />
        </div>
      )}
      {(locale == LOCALE_KO || locale == LOCALE_EN) && showGoogleTranslator && (
        <GoogleTranslate lessonId={lesson.id} onClose={() => setShowGoogleTranslator(false)} />
      )}

      {!isFetching && data && (
        <>
          {isMobile ? (
            <UnifiedTextbody
              lesson={lesson}
              currentUser={currentUser}
              isReview={isReview}
              currentTab={unifiendCurrentTab}
              setCurrentTab={setUnifiedCurrentTab}
              ringleDocToken={lesson?.ringledoc}
              isEditable={isEditable}
              setIsEditable={setIsEditable}
              setTabData={setTabData}
              answer={answer}
              setAnswer={setAnswer}
              selectedShareState={selectedShareState}
              setSelectedShareState={setSelectedShareState}
              activeQuestionId={activeQuestionId}
              setActiveQuestionId={setActiveQuestionId}
              saveMoveSubTab={saveMoveSubTab}
            />
          ) : (
            <Textbody
              lesson={lesson}
              currentUser={currentUser}
              isReview={isReview}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              currentTabSub={currentTabSub}
              setCurrentTabSub={setCurrentTabSub}
              ringleDocToken={lesson?.ringledoc}
              isEditable={isEditable}
              setIsEditable={setIsEditable}
              setTabData={setTabData}
              answer={answer}
              setAnswer={setAnswer}
              selectedShareState={selectedShareState}
              setSelectedShareState={setSelectedShareState}
              activeQuestionId={activeQuestionId}
              setActiveQuestionId={setActiveQuestionId}
              saveMoveSubTab={saveMoveSubTab}
            />
          )}
        </>
      )}
    </div>
  )
}

export default Preparation
